import { unit } from 'mathjs'

import { GeckoMeasurementSystem } from './geckoMeasurementSystem'
import { GeckoMeasurementUnit } from './geckoMeasurementUnit'

const toLengthUnit = (id: string): string | undefined => {
  if (id === 'in' || id === 'ft' || id === 'm' || id === 'mm') {
    return id
  }
  return undefined
}

const toLengthPerYearUnit = (id: string): string | undefined => {
  if (id === GeckoMeasurementUnit.InchesPerYear.id) return 'in'
  if (id === GeckoMeasurementUnit.MillimetersPerYear.id) return 'mm'

  if (id === GeckoMeasurementUnit.FeetPerSecond.id) return 'ft'
  if (id === GeckoMeasurementUnit.MetersPerSecond.id) return 'm'
  return undefined
}

const toPressureUnit = (id: string): string | undefined => {
  if (id === GeckoMeasurementUnit.PoundsForcePerSqInch.id) return 'psi'
  if (id === GeckoMeasurementUnit.Megapascals.id) return 'MPa'

  return undefined
}

const toAreaUnit = (id: string): string | undefined => {
  if (id === GeckoMeasurementUnit.SquareFeet.id) return 'sqft'
  if (id === GeckoMeasurementUnit.SquareMeters.id) return 'm2'
}

const toTemperatureUnit = (id: string): string | undefined => {
  if (id === GeckoMeasurementUnit.Fahrenheit.id) return 'fahrenheit'
  if (id === GeckoMeasurementUnit.Celsius.id) return 'celsius'
  return undefined
}

const toAngleUnit = (id: string): string | undefined => {
  if (id === GeckoMeasurementUnit.Radians.id) return 'rad'
  if (id === GeckoMeasurementUnit.Degrees.id) return 'deg'
  return undefined
}

export const convertNumber = (
  num: number,
  fromUnit: GeckoMeasurementUnit,
  toUnit: GeckoMeasurementUnit
): number => {
  try {
    const lengthStart = toLengthUnit(fromUnit.id)
    const lengthEnd = toLengthUnit(toUnit.id)
    if (lengthStart && lengthEnd)
      return unit(num, lengthStart).toNumber(lengthEnd)
    const lengthPerYearStart = toLengthPerYearUnit(fromUnit.id)
    const lengthPerYearEnd = toLengthPerYearUnit(toUnit.id)
    if (lengthPerYearStart && lengthPerYearEnd)
      return unit(num, lengthPerYearStart).toNumber(lengthPerYearEnd)
    const pressureStart = toPressureUnit(fromUnit.id)
    const pressureEnd = toPressureUnit(toUnit.id)
    if (pressureStart && pressureEnd)
      return unit(num, pressureStart).toNumber(pressureEnd)
    const temperatureStart = toTemperatureUnit(fromUnit.id)
    const temperatureEnd = toTemperatureUnit(toUnit.id)
    if (temperatureStart && temperatureEnd) {
      return unit(num, temperatureStart).toNumber(temperatureEnd)
    }
    const areaStart = toAreaUnit(fromUnit.id)
    const areaEnd = toAreaUnit(toUnit.id)
    if (areaStart && areaEnd) {
      return unit(num, areaStart).toNumber(areaEnd)
    }
    const angleStart = toAngleUnit(fromUnit.id)
    const angleEnd = toAngleUnit(toUnit.id)
    if (angleStart && angleEnd) {
      return unit(num, angleStart).toNumber(angleEnd)
    }
    return num
  } catch {
    return num
  }
}

export const lookUpPreferredUnit = (
  inputUnit: GeckoMeasurementUnit | undefined,
  measurementSystem: GeckoMeasurementSystem
) => {
  if (
    inputUnit === GeckoMeasurementUnit.InchesPerYear ||
    inputUnit === GeckoMeasurementUnit.MillimetersPerYear
  )
    return measurementSystem === GeckoMeasurementSystem.Metric
      ? GeckoMeasurementUnit.MillimetersPerYear
      : GeckoMeasurementUnit.InchesPerYear
  if (
    inputUnit === GeckoMeasurementUnit.FeetPerSecond ||
    inputUnit === GeckoMeasurementUnit.MetersPerSecond
  )
    return measurementSystem === GeckoMeasurementSystem.Metric
      ? GeckoMeasurementUnit.MetersPerSecond
      : GeckoMeasurementUnit.FeetPerSecond
  if (
    inputUnit === GeckoMeasurementUnit.Millimeters ||
    inputUnit === GeckoMeasurementUnit.Inches
  )
    return measurementSystem === GeckoMeasurementSystem.Metric
      ? GeckoMeasurementUnit.Millimeters
      : GeckoMeasurementUnit.Inches
  if (
    inputUnit === GeckoMeasurementUnit.Meters ||
    inputUnit === GeckoMeasurementUnit.Feet
  )
    return measurementSystem === GeckoMeasurementSystem.Metric
      ? GeckoMeasurementUnit.Meters
      : GeckoMeasurementUnit.Feet

  if (
    inputUnit === GeckoMeasurementUnit.Megapascals ||
    inputUnit === GeckoMeasurementUnit.PoundsForcePerSqInch
  )
    return measurementSystem === GeckoMeasurementSystem.Metric
      ? GeckoMeasurementUnit.Megapascals
      : GeckoMeasurementUnit.PoundsForcePerSqInch

  if (
    inputUnit === GeckoMeasurementUnit.Celsius ||
    inputUnit === GeckoMeasurementUnit.Fahrenheit
  )
    return measurementSystem === GeckoMeasurementSystem.Metric
      ? GeckoMeasurementUnit.Celsius
      : GeckoMeasurementUnit.Fahrenheit

  if (
    inputUnit === GeckoMeasurementUnit.Radians ||
    inputUnit === GeckoMeasurementUnit.Degrees
  )
    return measurementSystem === GeckoMeasurementSystem.Metric
      ? GeckoMeasurementUnit.Radians
      : GeckoMeasurementUnit.Degrees

  return inputUnit ?? GeckoMeasurementUnit.None
}
